import {
    init,
    setUser,
    setTags,
    EventHint,
    browserTracingIntegration,
    globalHandlersIntegration,
    captureConsoleIntegration,
    captureMessage,
    captureException,
    ErrorBoundary as SentryErrorBoundary,
    replayIntegration,
} from '@sentry/react';
import { isDebug } from '../@utils/webview';

import { getData } from '@umu/umu-util';
import { getCurrentSystem, isProd } from '@umu/ukit-lang';
import { getModuleData } from 'common/src/services';
import { getConfig } from '@umu/umu-api';
import { wrapErrorByMessage } from 'businessCommon/src/services/error';
import { createElement } from 'react';
import { safeJSONParse } from '../@utils/jsonParse';
import { getIgnoreErrorsList, innerBeforeSend, getJsContext } from './sentryIgnores';

// ==================== 网络质量检测工具 ====================

interface NetworkInfo {
    effectiveType: string;
    downlink: number;
    rtt: number;
    apiAvailable: boolean;
}

function estimateFromTiming(): { rtt: number; downlink: number } {
    try {
        if (typeof performance === 'undefined') return { rtt: -1, downlink: -1 };
        const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
        if (!navEntries.length) return { rtt: -1, downlink: -1 };
        const nav = navEntries[0];
        let rtt = nav.connectEnd - nav.connectStart;
        if (rtt <= 0) rtt = nav.responseStart - nav.requestStart;
        let downlink = -1;
        const resources = performance.getEntriesByType('resource') as PerformanceResourceTiming[];
        for (let i = 0; i < resources.length; i++) {
            const entry = resources[i];
            const dur = entry.responseEnd - entry.responseStart;
            if (entry.transferSize > 0 && dur > 10) {
                const tp = (entry.transferSize * 8) / (dur * 1000);
                if (tp > downlink) downlink = tp;
            }
        }
        const navDur = nav.responseEnd - nav.responseStart;
        if (nav.transferSize > 0 && navDur > 10) {
            const tp = (nav.transferSize * 8) / (navDur * 1000);
            if (tp > downlink) downlink = tp;
        }
        return { rtt: rtt > 0 ? rtt : -1, downlink };
    } catch (_) {
        return { rtt: -1, downlink: -1 };
    }
}

function getNetworkInfo(): NetworkInfo {
    const nav = navigator as any;
    const conn = nav.connection || nav.mozConnection || nav.webkitConnection;
    if (conn) {
        const rtt = typeof conn.rtt === 'number' ? conn.rtt : -1;
        const downlink = typeof conn.downlink === 'number' ? conn.downlink : -1;
        if (rtt !== 0 || downlink !== 0) {
            return { effectiveType: conn.effectiveType || 'unknown', downlink, rtt, apiAvailable: true };
        }
    }
    const timing = estimateFromTiming();
    return {
        effectiveType: (conn && conn.effectiveType) || 'unknown',
        downlink: timing.downlink,
        rtt: timing.rtt,
        apiAvailable: false,
    };
}

function isWeakNetwork(): boolean {
    const { effectiveType, rtt, downlink } = getNetworkInfo();
    if (['2g', 'slow-2g', '3g'].includes(effectiveType)) return true;
    if (rtt > 500) return true;
    if (downlink > 0 && downlink < 1) return true;
    return false;
}

function getNavigationTiming(): { connectTime: number; dnsTime: number } {
    try {
        if (typeof performance === 'undefined') return { connectTime: -1, dnsTime: -1 };
        const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
        if (!navEntries.length) return { connectTime: -1, dnsTime: -1 };
        const nav = navEntries[0];
        return {
            connectTime: nav.connectEnd - nav.connectStart,
            dnsTime: nav.domainLookupEnd - nav.domainLookupStart,
        };
    } catch (_) {
        return { connectTime: -1, dnsTime: -1 };
    }
}

function attachNetworkTags(event: any): void {
    const info = getNetworkInfo();
    if (!event.tags) event.tags = {};
    event.tags['network.effectiveType'] = info.effectiveType;
    event.tags['network.downlink'] = String(info.downlink);
    event.tags['network.rtt'] = String(info.rtt);
    event.tags['network.isWeak'] = String(isWeakNetwork());
    event.tags['network.apiAvailable'] = String(info.apiAvailable);
}

// ==================== 爬虫/Bot 流量检测 ====================

const BOT_PATTERNS = [
    /bot/i, /crawler/i, /spider/i, /headless/i,
    /puppeteer/i, /playwright/i, /phantom/i, /selenium/i,
    /scrapy/i, /yandex/i, /Sogou/i,
    /facebookexternalhit/i, /Slurp/i,
];

let _lastHiddenTimestamp = 0;
if (typeof document !== 'undefined') {
    if (document.visibilityState === 'hidden') _lastHiddenTimestamp = Date.now();
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') _lastHiddenTimestamp = Date.now();
    });
}

/**
 * 检测当前访问是否来自爬虫/Bot
 * 爬虫流量的性能数据不代表真实用户体验，会严重拉高 P75 指标
 */
function isBotTraffic(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    return BOT_PATTERNS.some((pattern) => pattern.test(ua));
}

const CurrentSystem = getCurrentSystem();
const SYSTEM_DNS_URL_MAP = {
    CN: 'https://de5cbf34dddb43e7a09553fb808cfd57@sentry.umu.cn/11',
    TW: 'https://3c30774713ac402fbe62cf5d4ff30886@sentry.umu.tw/4',
    JP: 'https://a124083f17e4411ab12341704975b8b7@sentry.umu.co/13',
    EN: 'https://3ee9857afe7345c8b5d6c484667a1a82@sentry.umu.com/4',
    EM: 'https://81c588d062bf4651b745442385f039ee@sentry.umu.com/14',
    ATES: '',
};

// 从apollo 获取 traces_sample_rate 的类
class TracesSampleRate {
    private umuId: string;
    private eId: string;
    private repo: string;
    private pageScope: string;
    private defaultReplaySampleRate: {
        replaysSessionSampleRate: number;
        replaysOnErrorSampleRate: number;
    };
    constructor(umuId: string, { repo, pageScope }: { repo: string; pageScope: string }) {
        this.umuId = umuId;
        const userInfo = getModuleData('userInfo');
        this.eId = getData(userInfo, 'enterprise_info', 'enterprise_id') || '0';

        this.repo = repo;
        this.pageScope = pageScope;
        this.defaultReplaySampleRate = {
            replaysSessionSampleRate: 0,
            replaysOnErrorSampleRate: 0,
        };
    }
    // get default traces sample rate
    private getDefaultTracesSampleRate = () => {
        let tracesSampleRate = 0.5;
        if (CurrentSystem === 'JP') {
            tracesSampleRate = 0.15;
        }
        return tracesSampleRate;
    };

    private async getTracesSampleRateConfig() {
        // 判断 userId 是否为空
        if (!this.umuId) {
            return null;
        }
        try {
            const res = await getConfig({ key: `sentry_all_log_open` });
            if (res?.data) {
                // 解析res.data， data 是 userid: traces_sample_rate 的格式
                const data = safeJSONParse(res.data, {} as Record<string, number>);
                return Number(data?.[this.umuId]);
            }
            return null;
        } catch (error) {
            console.error('getTracesSampleRateConfig error', error);
            return null;
        }
    }

    private async getReplaysSampleRateConfig() {
        if (!this.umuId && !this.eId) {
            return this.defaultReplaySampleRate;
        }
        try {
            const res = await getConfig({ key: `sentry_replay_open` });
            if (res?.data) {
                const data = safeJSONParse(res.data, {} as Record<string, any>);
                return (
                    data?.users?.[this.umuId]?.pages?.[this.repo + '/' + this.pageScope] ||
                    data?.enterprises?.[this.eId]?.pages?.[this.repo + '/' + this.pageScope] ||
                    data?.default ||
                    this.defaultReplaySampleRate
                );
            }
            return this.defaultReplaySampleRate;
        } catch (error) {
            console.error('getReplaysSampleRateConfig error', error);
            return this.defaultReplaySampleRate;
        }
    }

    public isValidateRate(rate: number) {
        return rate && !isNaN(rate) && rate >= 0 && rate <= 1;
    }

    public getTracesSampleRate = async () => {
        const tracesSampleRate = {
            tracesSampleRate: this.getDefaultTracesSampleRate(),
            replaysSessionSampleRate: this.defaultReplaySampleRate.replaysSessionSampleRate,
            replaysOnErrorSampleRate: this.defaultReplaySampleRate.replaysOnErrorSampleRate,
        };
        const [rate, replaysSampleRate] = await Promise.all([
            this.getTracesSampleRateConfig(),
            this.getReplaysSampleRateConfig(),
        ]);
        if (this.isValidateRate(Number(rate))) {
            tracesSampleRate.tracesSampleRate = Number(rate);
        }
        if (
            replaysSampleRate?.replaysSessionSampleRate &&
            this.isValidateRate(Number(replaysSampleRate.replaysSessionSampleRate))
        ) {
            tracesSampleRate.replaysSessionSampleRate = Number(replaysSampleRate.replaysSessionSampleRate);
        }
        if (
            replaysSampleRate?.replaysOnErrorSampleRate &&
            this.isValidateRate(Number(replaysSampleRate.replaysOnErrorSampleRate))
        ) {
            tracesSampleRate.replaysOnErrorSampleRate = Number(replaysSampleRate.replaysOnErrorSampleRate);
        }
        return tracesSampleRate;
    };
}

// get sentry release
const getSentryRelease = () => {
    const version = (window as any).umu_version;
    const sentryRelease = `mono_${version}`;
    return sentryRelease;
};

// get deny urls
const getDenyUrls = () => {
    return [
        /^chrome:/i,
        /chrome-extension:/i,
        /webkit-masked-url:\/\/hidden/i,
        /beschannels\.com/i,
        /googletagmanager\.com/i,
    ];
};

class DynamicSentryIgnoreErrorsManager {
    private static instance: DynamicSentryIgnoreErrorsManager;
    private ignoreErrors: string[] = [];
    private initPromise: Promise<void> | null = null;

    private constructor() {}

    public static getInstance(): DynamicSentryIgnoreErrorsManager {
        if (!DynamicSentryIgnoreErrorsManager.instance) {
            DynamicSentryIgnoreErrorsManager.instance = new DynamicSentryIgnoreErrorsManager();
        }
        return DynamicSentryIgnoreErrorsManager.instance;
    }

    public async initialize(): Promise<void> {
        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = this.doInitialize();
        return this.initPromise;
    }

    private async doInitialize(): Promise<void> {
        try {
            const res = await getConfig({ key: `sentry_ignore_list_url` });
            const url = res?.data || '';
            if (url) {
                const response = await fetch(url);
                const data = await response.json();
                if (data && Array.isArray(data)) {
                    this.ignoreErrors = data;
                }
            }
        } catch (error) {
            console.error('DynamicSentryIgnoreErrorsManager initialize error', error);
        }
    }

    public getIgnoreErrors(): string[] {
        return this.ignoreErrors;
    }
}

const beforeSend = (event: any, _hint: EventHint) => {
    const manager = DynamicSentryIgnoreErrorsManager.getInstance();
    return innerBeforeSend(event, _hint, manager.getIgnoreErrors());
};

const getEnvironment = (isProd: boolean, tracesSampleRate: number) => {
    if (isProd && tracesSampleRate === 1) {
        return 'full-trace';
    }
    return isProd ? 'pro' : 'dev';
};

function moduleEntrySort(moduleA: string[], moduleB: string[]) {
    const MODULE_PRIORITY_ORDER = ['twww', 'platform'];
    const indexA = MODULE_PRIORITY_ORDER.indexOf(moduleA[0]);
    const indexB = MODULE_PRIORITY_ORDER.indexOf(moduleB[0]);

    // 将 -1 (不在列表中) 替换为列表长度，确保排到最后
    const priorityA = indexA === -1 ? MODULE_PRIORITY_ORDER.length : indexA;
    const priorityB = indexB === -1 ? MODULE_PRIORITY_ORDER.length : indexB;

    // 否则按优先级数字排序（数字越小优先级越高）
    return priorityA - priorityB;
}
/**
 * 获取当前页面的作用域信息
 */
const getPageScope = () => {
    try {
        // 例如：{ "learning_task/my-task": {}, "twww/courseManage": {}, "platform/platform": {} }
        const langObj = (window as any)?.__LANG__ || {};
        if (!langObj || typeof langObj !== 'object' || Object.keys(langObj).length === 0) {
            return { repo: '', pageScope: '' };
        }
        const moduleEntries = Object.keys(langObj)
            .map((key) => {
                return key.split('/');
            })
            .filter((keys) => keys.length > 1)
            .sort(moduleEntrySort);
        if (moduleEntries.length === 0) {
            return { repo: '', pageScope: '' };
        }
        const [repo, pageScope] = moduleEntries[0];
        return {
            repo: repo || '',
            pageScope: pageScope || '',
        };
    } catch (error) {
        // 异常处理：避免页面作用域获取失败影响整个 Sentry 初始化流程
        console.error('getPageScope error', error);
        return { repo: '', pageScope: '' };
    }
};

const getSentryParams = () => {
    const userInfo = getModuleData('userInfo');
    const eId = getData(userInfo, 'enterprise_info', 'enterprise_id') || '0';
    const eName = getData(userInfo, 'enterprise_info', 'show_name') || '';
    const userName = userInfo?.user_name || '';
    const email = userInfo?.email || '';
    const umuId = userInfo?.umu_id || '';
    const studentId = userInfo?.student_id || '';
    const jsContext = getJsContext();
    const params: Record<string, string> = {
        eId,
        eName,
        umuId,
        studentId,
        userName,
        email,
        jsContext,
        pageScope: getPageScope().pageScope,
        repo: getPageScope().repo,
    };
    return params;
};

// 初始化Sentry配置
const initSentryConfig = (
    tracesSampleRate: number,
    replaysSessionSampleRate: number,
    replaysOnErrorSampleRate: number,
) => {
    const sentryDSN = SYSTEM_DNS_URL_MAP[CurrentSystem as keyof typeof SYSTEM_DNS_URL_MAP] || '';
    if (!sentryDSN) {
        return;
    }
    // 异步初始化动态错误列表，不阻塞Sentry初始化
    const manager = DynamicSentryIgnoreErrorsManager.getInstance();
    manager.initialize(); // 不await，保持原有的非阻塞行为
    init({
        environment: getEnvironment(isProd, tracesSampleRate),
        dsn: sentryDSN,
        integrations: [
            browserTracingIntegration(),
            globalHandlersIntegration({
                onunhandledrejection: true,
                onerror: true,
            }),
            captureConsoleIntegration({ levels: ['error'] }),
            replayIntegration({
                maskAllText: false,
                maskAllInputs: false,
                networkDetailAllowUrls: ['/megrez/exam/v1/saveAnswer', '/megrez/exam/v1/submitExam'],
                networkRequestHeaders: ['X-Custom-Header'],
                networkResponseHeaders: ['X-Custom-Header'],
            }),
        ],
        ignoreErrors: tracesSampleRate === 1 ? [] : getIgnoreErrorsList(),
        denyUrls: getDenyUrls(),
        // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
        // We recommend adjusting this value in production
        release: getSentryRelease(),
        maxBreadcrumbs: 50,
        tracesSampleRate: tracesSampleRate,
        replaysSessionSampleRate: replaysSessionSampleRate,
        replaysOnErrorSampleRate: replaysOnErrorSampleRate,
        // https://docs.sentry.io/platforms/javascript/configuration/filtering/
        beforeSend: (event: any, hint: EventHint) => {
            // 为错误事件附加网络质量标签（用于分析，不过滤）
            attachNetworkTags(event);
            // 过滤爬虫/Bot 流量的错误事件
            if (isBotTraffic()) return null;
            // 全量采集模式不过滤错误
            if (tracesSampleRate === 1) return event;
            return beforeSend(event, hint);
        },
        // 过滤弱网环境 & 爬虫流量的性能事务数据，避免脏数据影响 P75 指标
        beforeSendTransaction: (event: any) => {
            attachNetworkTags(event);
            if (!event.tags) event.tags = {};
            const txStartMs = (event.start_timestamp || 0) * 1000;
            const wasHiddenDuringTx = _lastHiddenTimestamp > 0 && _lastHiddenTimestamp >= txStartMs;
            const ttfb = event.measurements?.ttfb?.value;
            const navTiming = getNavigationTiming();
            event.tags['page.wasBackgrounded'] = String(wasHiddenDuringTx);
            event.tags['network.connectTime'] = String(navTiming.connectTime);
            event.tags['network.dnsTime'] = String(navTiming.dnsTime);
            if (isBotTraffic()) {
                if (isDebug()) {
                    console.log('[Sentry] Bot 流量，丢弃性能事务:', navigator.userAgent);
                }
                return null;
            }
            if (wasHiddenDuringTx || (typeof ttfb === 'number' && ttfb > 10000)) {
                if (isDebug()) {
                    console.log('[Sentry] 疑似后台标签页，丢弃性能事务');
                }
                return null;
            }
            if (isWeakNetwork()) {
                if (isDebug()) {
                    console.log('[Sentry] 弱网环境，丢弃性能事务:', getNetworkInfo());
                }
                return null;
            }
            if (navTiming.connectTime > 3000 || navTiming.dnsTime > 3000) {
                if (isDebug()) {
                    console.log('[Sentry] 网络连接异常（connect/DNS > 3s），丢弃性能事务:', navTiming);
                }
                return null;
            }
            // 兜底：前置过滤均通过但 LCP 仍超 15s，视为非正常加载（CDN 故障、浏览器冻结等）
            // 兼容 Sentry SDK v7（key: 'largest-contentful-paint'）和 v8（key: 'lcp'）两种命名
            const lcpMeasurement = event.measurements?.['lcp'] ?? event.measurements?.['largest-contentful-paint'];
            const lcp = lcpMeasurement?.value;
            if (typeof lcp === 'number' && lcp > 15000) {
                if (isDebug()) {
                    console.log('[Sentry] LCP 异常（> 15s），丢弃性能事务:', lcp);
                }
                return null;
            }
            return event;
        },
    });
    // window.Sentry = Sentry;
    // 目前全局只有两个方法上报错误，有需要再在window中注入
    (window as any).Sentry = {
        captureException: captureException,
        captureMessage: captureMessage,
    };

    const params = getSentryParams();
    setUser(params);
    setTags(params);
};

export const initSentry = (umuId: string) => {
    const tracesSampleRate = new TracesSampleRate(umuId, getPageScope());
    tracesSampleRate
        .getTracesSampleRate()
        .then(({ tracesSampleRate, replaysSessionSampleRate, replaysOnErrorSampleRate }) => {
            if (isDebug()) {
                console.log('Sentry init tracesSampleRate', tracesSampleRate);
                console.log('Sentry init replaysSessionSampleRate', replaysSessionSampleRate);
                console.log('Sentry init replaysOnErrorSampleRate', replaysOnErrorSampleRate);
            }
            initSentryConfig(tracesSampleRate, replaysSessionSampleRate, replaysOnErrorSampleRate);
        })
        .catch((error) => {
            console.error('initSentry error', error);
        });
};

export function captureSentryException(error: Error, message: string = '', extra: Record<string, any> = {}): void {
    if (!error) {
        return;
    }
    const wrappedError = wrapErrorByMessage(error, message);
    if (window.Sentry) {
        // 序列化 error 对象，保留所有属性（包括自定义属性）
        let errorResourceSerialized: string | undefined;
        try {
            errorResourceSerialized = JSON.stringify(error, Object.getOwnPropertyNames(error));
        } catch (e) {
            // 如果序列化失败（循环引用等），使用简单版本
            errorResourceSerialized = String(error);
        }

        window.Sentry.captureException(wrappedError, {
            extra: {
                errorResource: error,  // Sentry 原生处理的 Error 对象
                errorResourceSerialized: errorResourceSerialized,  // 完整序列化版本
                ...extra,
            },
            mechanism: {
                type: 'generic',
                handled: true,
            },
        });
    }
}

export function captureSentryMessage(
    message: string,
    options: {
        level?: 'fatal' | 'error' | 'warning' | 'info' | 'debug';
        extra?: Record<string, any>;
    } = {}
): void {
    if (!message) {
        return;
    }
    if (window.Sentry) {
        window.Sentry.captureMessage(message, {
            level: options.level || 'info',
            extra: {
                ...options.extra,
            },
        });
    }
}

export const wrapWithErrorBoundary = (children: React.ReactNode) => {
    // 收集错误信息并发送到Sentry
    return createElement(
        SentryErrorBoundary,
        {
            beforeCapture: (scope) => {
                // 设置统一的标签
                scope.setTag('errorSource', 'ReactErrorBoundary');
            },
        },
        children,
    );
};

export const ErrorBoundary = ({ children }: { children: React.ReactNode }) => {
    return wrapWithErrorBoundary(children);
};
